// Custom TipTap nodes/extensions for the KB editor.
//   • BlockLayout  — indent + line spacing on paragraphs and headings
//   • KbImage      — image with alt text, caption (figure/figcaption) and width
//   • KbEmbed      — responsive iframe (video / PDF / any allowed embed)
//   • KbAudio      — <audio controls> for uploaded audio
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import { Image } from "@tiptap/extension-image";
import { calloutTitleDefault } from "@/lib/kbCalloutStyles";

export const INDENT_STEP = 32;
export const LINE_HEIGHTS = ["", "1", "1.15", "1.5", "2"];

export const BlockLayout = Extension.create({
  name: "blockLayout",
  addGlobalAttributes() {
    return [{
      types: ["paragraph", "heading", "blockquote", "listItem"],
      attributes: {
        indent: {
          default: 0,
          parseHTML: (el) => parseInt(el.style.marginLeft || 0, 10) / INDENT_STEP || 0,
          renderHTML: (attrs) => (attrs.indent
            ? { style: `margin-left: ${attrs.indent * INDENT_STEP}px` } : {}),
        },
        lineHeight: {
          default: "",
          parseHTML: (el) => el.style.lineHeight || "",
          renderHTML: (attrs) => (attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {}),
        },
      },
    }];
  },
  addCommands() {
    const shift = (dir) => () => ({ state, tr, dispatch }) => {
      const { from, to } = state.selection;
      let changed = false;
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.spec.attrs && "indent" in node.type.spec.attrs) {
          const next = Math.max(0, Math.min(8, (node.attrs.indent || 0) + dir));
          if (next !== node.attrs.indent) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
            changed = true;
          }
        }
      });
      if (changed && dispatch) dispatch(tr);
      return changed;
    };
    return {
      indentBlock: shift(1),
      outdentBlock: shift(-1),
      setLineHeight: (value) => ({ commands }) => ["paragraph", "heading", "blockquote"]
        .map((t) => commands.updateAttributes(t, { lineHeight: value })).some(Boolean),
    };
  },
});

export const KbImage = Image.extend({
  name: "image",
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute("width") || el.style.width || null,
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
      },
      caption: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-caption")
          || el.parentElement?.querySelector("figcaption")?.textContent || "",
        renderHTML: () => ({}),
      },
      // Iter 177 — images could only ever sit centred. `align` rides on a
      // class so it survives the sanitiser and styles the same in the editor,
      // the article and the print pack.
      align: {
        default: null,
        parseHTML: (el) => {
          const cls = el.getAttribute("class") || "";
          const m = cls.match(/kb-img-(left|center|right)/);
          if (m) return m[1];
          const own = el.style?.float || el.getAttribute("align");
          return own === "left" || own === "right" ? own : null;
        },
        renderHTML: (attrs) => (attrs.align ? { class: `kb-img-${attrs.align}` } : {}),
      },
      // Iter 187 — aligning a picture used to float it, so the paragraph after
      // it jumped up beside it whether or not that was wanted. Alignment now
      // only moves the picture on its own line; text sits beside it only when
      // the editor explicitly asks for it.
      wrap: {
        default: null,
        parseHTML: (el) => ((el.getAttribute("class") || "").includes("kb-img-wrap")
          ? true : null),
        renderHTML: (attrs) => (attrs.wrap ? { class: "kb-img-wrap" } : {}),
      },
      // How much room a wrapped picture takes — narrow / half / wide.
      wrapWidth: {
        default: null,
        parseHTML: (el) => ((el.getAttribute("class") || "")
          .match(/kb-img-w-(narrow|half|wide)/) || [])[1] || null,
        renderHTML: (attrs) => (attrs.wrapWidth
          ? { class: `kb-img-w-${attrs.wrapWidth}` } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "figure.kb-figure img" }, { tag: "img[src]" }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const img = ["img", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
    if (!node.attrs.caption) return img;
    const align = node.attrs.align ? ` kb-img-${node.attrs.align}` : "";
    const wrap = node.attrs.wrap ? " kb-img-wrap" : "";
    const ww = node.attrs.wrapWidth ? ` kb-img-w-${node.attrs.wrapWidth}` : "";
    return ["figure", { class: `kb-figure${align}${wrap}${ww}` }, img,
      ["figcaption", {}, node.attrs.caption]];
  },
});

export const KbEmbed = Node.create({
  name: "kbEmbed",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    // The src/title live on the CHILD iframe, so each attribute has to read
    // the wrapper *and* the iframe. Without this the editor parsed a saved
    // embed back with an empty src and the video died on the next save.
    const iframeOf = (el) => (el.tagName === "IFRAME" ? el : el.querySelector("iframe"));
    return {
      src: {
        default: "",
        parseHTML: (el) => iframeOf(el)?.getAttribute("src") || el.getAttribute("data-kb-src") || "",
      },
      kind: {
        default: "video",
        parseHTML: (el) => {
          const own = el.getAttribute("data-kb-kind");
          if (own) return own;
          const src = (iframeOf(el)?.getAttribute("src") || "").toLowerCase();
          return src.split("?")[0].endsWith(".pdf") ? "pdf" : "video";
        },
      },
      title: {
        default: "",
        parseHTML: (el) => iframeOf(el)?.getAttribute("title") || el.getAttribute("data-kb-title") || "",
      },
      height: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-kb-height")
          || iframeOf(el)?.getAttribute("height") || "",
      },
    };
  },
  parseHTML() {
    return [
      { tag: "div[data-kb-block='embed']" },
      // Imported HelpJuice articles carry a bare <iframe> (often inside a
      // padding-hack wrapper). With no rule for it TipTap had no node to put
      // it in and silently dropped every imported video on the first save.
      { tag: "iframe[src]", priority: 60 },
    ];
  },
  renderHTML({ node }) {
    const a = node.attrs;
    const kind = a.kind || "video";
    return ["div", {
      class: `kb-embed kb-embed-${kind}`,
      "data-kb-block": "embed",
      "data-kb-kind": kind,
      ...(a.height ? { "data-kb-height": String(a.height) } : {}),
    }, ["iframe", {
      src: a.src, title: a.title || kind, loading: "lazy",
      allow: "accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen",
      allowfullscreen: "true", frameborder: "0",
      ...(a.height ? { height: a.height } : {}),
    }]];
  },
  addCommands() {
    return {
      insertEmbed: (attrs) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export const KbAudio = Node.create({
  name: "kbAudio",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { src: { default: "" }, name: { default: "" } };
  },
  parseHTML() {
    return [{ tag: "audio[src]" }];
  },
  renderHTML({ HTMLAttributes: a }) {
    return ["audio", {
      src: a.src, controls: "true", preload: "metadata",
      class: "kb-audio", "data-name": a.name || "",
    }];
  },
  addCommands() {
    return {
      insertAudio: (attrs) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export const KbVideo = Node.create({
  name: "kbVideo",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { src: { default: "" }, name: { default: "" }, poster: { default: "" } };
  },
  parseHTML() {
    return [{ tag: "video[src]" }];
  },
  renderHTML({ HTMLAttributes: a }) {
    return ["video", {
      src: a.src, controls: "true", preload: "metadata", playsinline: "true",
      class: "kb-video", "data-name": a.name || "",
      ...(a.poster ? { poster: a.poster } : {}),
    }];
  },
  addCommands() {
    return {
      insertVideo: (attrs) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

/** A file hanging off an article — a spec, a CSV, a signed form. Rendered as a
 *  plain anchor so it works in the reader, in print and in an emailed copy. */
export const KbAttachment = Node.create({
  name: "kbAttachment",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { href: { default: "" }, name: { default: "" }, size: { default: "" }, ext: { default: "" } };
  },
  parseHTML() {
    return [{ tag: "a[data-kb-block='attachment']" }];
  },
  renderHTML({ HTMLAttributes: a }) {
    return ["a", {
      href: a.href, class: "kb-attachment", "data-kb-block": "attachment",
      "data-ext": (a.ext || "").toLowerCase(), download: "",
      target: "_blank", rel: "noreferrer",
    },
    ["span", { class: "kb-attachment-ext" }, (a.ext || "file").toUpperCase().slice(0, 4)],
    ["span", { class: "kb-attachment-name" }, a.name || "Attachment"],
    ["span", { class: "kb-attachment-meta" }, a.size ? `${a.size} · download` : "download"]];
  },
  addCommands() {
    return {
      insertAttachment: (attrs) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

// ── Structured content blocks (Round 2b) ──────────────────────────────
export const CALLOUT_VARIANTS = [
  "note", "important", "tip", "best-practice", "prerequisite",
  "example", "success", "warning", "danger", "do", "dont",
];
// Grouped for the insert menu, in the order editors think about them.
export const CALLOUT_GROUPS = [
  ["Information", ["note", "important", "example"]],
  ["Guidance", ["tip", "best-practice", "prerequisite"]],
  ["Outcome", ["success", "warning", "danger"]],
  ["Contrast", ["do", "dont"]],
];
// Labels for the editor menu; unknown variants fall back to "note" on render.
export const CALLOUT_LABELS = {
  note: "Note", important: "Important", tip: "Tip", "best-practice": "Best practice",
  prerequisite: "Prerequisite", example: "Example", success: "Success",
  warning: "Warning", danger: "Danger", do: "Do", dont: "Don't",
};

export const KbCallout = Node.create({
  name: "kbCallout",
  group: "block",
  content: "block+",
  defining: true,
  addAttributes() {
    return { variant: { default: "note" }, title: { default: "" } };
  },
  parseHTML() {
    return [{
      tag: "div[data-kb-block='callout']",
      getAttrs: (el) => ({
        variant: el.getAttribute("data-kb-variant") || "note",
        title: el.getAttribute("data-kb-title") || "",
      }),
      // Older callouts have no body wrapper, so fall back to the whole box.
      contentElement: (el) => el.querySelector(".kb-callout-body") || el,
    }];
  },
  renderHTML({ HTMLAttributes: a }) {
    const variant = CALLOUT_VARIANTS.includes(a.variant) ? a.variant : "note";
    const attrs = {
      class: `kb-callout kb-callout-${variant}`,
      "data-kb-block": "callout", "data-kb-variant": variant,
      ...(a.title ? { "data-kb-title": a.title } : {}),
    };
    if (!a.title) return ["div", attrs, ["div", { class: "kb-callout-body" }, 0]];
    return ["div", attrs,
      ["div", { class: "kb-callout-title" }, a.title],
      ["div", { class: "kb-callout-body" }, 0]];
  },
  addCommands() {
    return {
      insertCallout: (variant = "note") => ({ commands }) => commands.insertContent({
        type: this.name,
        attrs: { variant, title: calloutTitleDefault(variant) },
        content: [{ type: "paragraph", content: [{ type: "text", text: " " }] }],
      }),
      setCalloutVariant: (variant) => ({ commands }) => commands.updateAttributes(this.name, { variant }),
      setCalloutTitle: (title) => ({ commands }) => commands.updateAttributes(this.name, { title }),
    };
  },
});

export const KbAccordion = Node.create({
  name: "kbAccordion",
  group: "block",
  content: "block+",
  defining: true,
  addAttributes() {
    return { title: { default: "Section title" }, open: { default: false } };
  },
  parseHTML() {
    return [{
      tag: "details[data-kb-block='accordion']",
      getAttrs: (el) => ({
        title: el.querySelector("summary")?.textContent?.trim()
          || el.getAttribute("data-kb-title") || "Section title",
        open: el.hasAttribute("open"),
      }),
      contentElement: (el) => el.querySelector(".kb-accordion-body") || el,
    }];
  },
  renderHTML({ HTMLAttributes: a }) {
    return ["details", {
      class: "kb-accordion", "data-kb-block": "accordion",
      "data-kb-title": a.title || "Section title", ...(a.open ? { open: "true" } : {}),
    }, ["summary", {}, a.title || "Section title"],
      ["div", { class: "kb-accordion-body" }, 0]];
  },
  // In the editor a native <details> stays collapsed (ProseMirror swallows the
  // summary click), so the body could never be reached. Edit through an
  // always-open view instead: inline title, "open by default" toggle, remove.
  addNodeView() {
    return ({ node, getPos, editor }) => {
      let current = node;
      const dom = document.createElement("div");
      dom.className = "kb-accordion kb-accordion-editing";
      dom.setAttribute("data-kb-block", "accordion");
      dom.setAttribute("data-testid", "kb-accordion-block");

      const head = document.createElement("div");
      head.className = "kb-accordion-head";
      head.contentEditable = "false";

      const chevron = document.createElement("span");
      chevron.className = "kb-accordion-chevron";
      chevron.textContent = "\u25B8";
      chevron.title = "Accordion — readers click the title to expand this section";

      const input = document.createElement("input");
      input.className = "kb-accordion-title-input";
      input.placeholder = "Section title";
      input.value = node.attrs.title || "";
      input.setAttribute("data-testid", "kb-accordion-inline-title");

      const setAttrs = (patch) => {
        const pos = getPos();
        if (typeof pos !== "number") return;
        editor.view.dispatch(editor.view.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, ...patch }));
      };
      input.addEventListener("input", () => setAttrs({ title: input.value }));
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === "ArrowDown") {
          e.preventDefault();
          const pos = getPos();
          if (typeof pos === "number") editor.chain().focus(pos + 2).run();
        }
      });

      const openLabel = document.createElement("label");
      openLabel.className = "kb-accordion-open";
      const openBox = document.createElement("input");
      openBox.type = "checkbox";
      openBox.checked = !!node.attrs.open;
      openBox.setAttribute("data-testid", "kb-accordion-open-toggle");
      openBox.addEventListener("change", () => setAttrs({ open: openBox.checked }));
      openLabel.append(openBox, document.createTextNode(" Open by default"));

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "kb-accordion-remove";
      remove.title = "Remove the accordion but keep its content";
      remove.textContent = "\u00D7";
      remove.setAttribute("data-testid", "kb-accordion-unwrap");
      remove.addEventListener("click", () => {
        const pos = getPos();
        if (typeof pos !== "number") return;
        editor.view.dispatch(editor.view.state.tr.replaceWith(pos, pos + current.nodeSize, current.content));
        editor.commands.focus();
      });

      head.append(chevron, input, openLabel, remove);
      const body = document.createElement("div");
      body.className = "kb-accordion-body";
      dom.append(head, body);

      return {
        dom,
        contentDOM: body,
        update(updated) {
          if (updated.type !== node.type) return false;
          current = updated;
          if (document.activeElement !== input) input.value = updated.attrs.title || "";
          openBox.checked = !!updated.attrs.open;
          return true;
        },
        stopEvent: (e) => head.contains(e.target),
        ignoreMutation: (m) => head.contains(m.target),
      };
    };
  },
  addCommands() {
    return {
      insertAccordion: () => ({ commands }) => commands.insertContent({
        type: this.name, attrs: { title: "Section title" },
        content: [{ type: "paragraph", content: [{ type: "text", text: " " }] }],
      }),
      // Lift the section's content out and drop the wrapper.
      unwrapAccordion: () => ({ state, tr, dispatch }) => {
        const { $from } = state.selection;
        for (let d = $from.depth; d > 0; d -= 1) {
          const n = $from.node(d);
          if (n.type.name === this.name) {
            if (dispatch) {
              const pos = $from.before(d);
              dispatch(tr.replaceWith(pos, pos + n.nodeSize, n.content));
            }
            return true;
          }
        }
        return false;
      },
    };
  },
});

export const KbTabPanel = Node.create({
  name: "kbTabPanel",
  content: "block+",
  defining: true,
  addAttributes() {
    return { title: { default: "Tab" } };
  },
  parseHTML() {
    return [{
      tag: "div[data-kb-block='tab']",
      getAttrs: (el) => ({ title: el.getAttribute("data-kb-title") || "Tab" }),
    }];
  },
  renderHTML({ HTMLAttributes: a }) {
    return ["div", {
      class: "kb-tab-panel", "data-kb-block": "tab",
      "data-kb-title": a.title || "Tab",
    }, 0];
  },
});

export const KbTabs = Node.create({
  name: "kbTabs",
  group: "block",
  content: "kbTabPanel+",
  defining: true,
  parseHTML() {
    return [{ tag: "div[data-kb-block='tabs']" }];
  },
  renderHTML() {
    return ["div", { class: "kb-tabs", "data-kb-block": "tabs" }, 0];
  },
  addCommands() {
    const panel = (title) => ({
      type: "kbTabPanel", attrs: { title },
      content: [{ type: "paragraph", content: [{ type: "text", text: " " }] }],
    });
    return {
      insertTabs: () => ({ commands }) => commands.insertContent({
        type: this.name, content: [panel("Tab 1"), panel("Tab 2")],
      }),
      addTabPanel: () => ({ state, commands }) => {
        const { $from } = state.selection;
        for (let d = $from.depth; d > 0; d -= 1) {
          if ($from.node(d).type.name === "kbTabs") {
            const end = $from.end(d);
            return commands.insertContentAt(end, panel(`Tab ${$from.node(d).childCount + 1}`));
          }
        }
        return false;
      },
    };
  },
});

export const KbColumn = Node.create({
  name: "kbColumn",
  content: "block+",
  defining: true,
  parseHTML() {
    return [{ tag: "div[data-kb-block='column']" }];
  },
  renderHTML() {
    return ["div", { class: "kb-column", "data-kb-block": "column" }, 0];
  },
});

export const KbColumns = Node.create({
  name: "kbColumns",
  group: "block",
  content: "kbColumn+",
  defining: true,
  parseHTML() {
    return [{ tag: "div[data-kb-block='columns']" }];
  },
  renderHTML({ node }) {
    return ["div", {
      class: "kb-columns", "data-kb-block": "columns",
      "data-kb-kind": String(node.childCount || 2),
    }, 0];
  },
  addCommands() {
    const col = () => ({
      type: "kbColumn", content: [{ type: "paragraph", content: [{ type: "text", text: " " }] }],
    });
    return {
      insertColumns: (count = 2) => ({ commands }) => commands.insertContent({
        type: this.name, content: Array.from({ length: Math.max(2, Math.min(4, count)) }, col),
      }),
      // Do / Don't side by side for quick contrast.
      insertCalloutPair: () => ({ commands }) => commands.insertContent({
        type: this.name,
        content: [["do", "Do"], ["dont", "Don't"]].map(([variant, title]) => ({
          type: "kbColumn",
          content: [{
            type: "kbCallout", attrs: { variant, title },
            content: [{ type: "paragraph", content: [{ type: "text", text: " " }] }],
          }],
        })),
      }),
    };
  },
});

export const KbEquation = Node.create({
  name: "kbEquation",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { latex: { default: "" } };
  },
  parseHTML() {
    return [{
      tag: "div[data-kb-block='equation']",
      getAttrs: (el) => ({ latex: el.getAttribute("data-kb-latex") || el.textContent || "" }),
    }];
  },
  renderHTML({ HTMLAttributes: a }) {
    return ["div", {
      class: "kb-equation", "data-kb-block": "equation", "data-kb-latex": a.latex || "",
    }, a.latex || ""];
  },
  addCommands() {
    return {
      insertEquation: (latex) => ({ commands }) => commands.insertContent({
        type: this.name, attrs: { latex },
      }),
    };
  },
});

export const KbToc = Node.create({
  name: "kbToc",
  group: "block",
  atom: true,
  parseHTML() {
    return [{ tag: "div[data-kb-block='toc']" }];
  },
  renderHTML() {
    return ["div", { class: "kb-toc", "data-kb-block": "toc" },
      ["p", { class: "kb-toc-fallback" }, "Contents"]];
  },
  addCommands() {
    return {
      insertToc: () => ({ commands }) => commands.insertContent({ type: this.name }),
    };
  },
});

// ── Diagrams + reusable snippet blocks (Round 2c) ─────────────────────
export const MERMAID_TEMPLATES = {
  "Call flow": `flowchart TD
  A[Inbound call to main number] --> B{Business hours?}
  B -- Yes --> C[Auto attendant]
  B -- No --> D[After-hours greeting]
  C --> E{Caller picks option}
  E -- 1 Sales --> F[Sales ring group]
  E -- 2 Support --> G[Support queue]
  F --> H{Answered?}
  G --> H
  H -- No --> I[Voicemail to email]`,
  Flowchart: `flowchart LR
  Start([Start]) --> Step1[Do the thing]
  Step1 --> Check{Worked?}
  Check -- Yes --> Done([Done])
  Check -- No --> Fix[Try the fallback] --> Check`,
  "Sequence diagram": `sequenceDiagram
  participant Customer
  participant Conduit
  participant Carrier
  Customer->>Conduit: Submits port request
  Conduit->>Carrier: Sends LNP form
  Carrier-->>Conduit: FOC date
  Conduit-->>Customer: Confirms cutover window`,
  "Decision tree": `flowchart TD
  Q1{Is the phone registered?} -- No --> A1[Check network + provisioning]
  Q1 -- Yes --> Q2{One-way audio?}
  Q2 -- Yes --> A2[Check NAT / SIP ALG]
  Q2 -- No --> A3[Escalate with a SIP trace]`,
  "Org chart": `flowchart TD
  CEO[Leadership] --> Ops[Operations]
  CEO --> Eng[Engineering]
  CEO --> Sales[Sales]
  Ops --> SD[Service Delivery]
  Ops --> Sup[Support]`,
};

export const KbMermaid = Node.create({
  name: "kbMermaid",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { code: { default: "" }, caption: { default: "" } };
  },
  parseHTML() {
    return [{
      tag: "div[data-kb-block='mermaid']",
      getAttrs: (el) => ({
        code: (el.querySelector("pre.kb-mermaid-src")?.textContent
          || el.getAttribute("data-kb-code") || "").trim(),
        caption: el.getAttribute("data-kb-title") || "",
      }),
    }];
  },
  renderHTML({ HTMLAttributes: a }) {
    return ["div", {
      class: "kb-mermaid", "data-kb-block": "mermaid",
      "data-kb-code": a.code || "",
      ...(a.caption ? { "data-kb-title": a.caption } : {}),
    }, ["pre", { class: "kb-mermaid-src" }, a.code || ""]];
  },
  addCommands() {
    return {
      insertDiagram: (code, caption = "") => ({ commands }) => commands.insertContent({
        type: this.name, attrs: { code, caption },
      }),
      updateDiagram: (attrs) => ({ commands }) => commands.updateAttributes(this.name, attrs),
    };
  },
});

export const KbSnippetRef = Node.create({
  name: "kbSnippetRef",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { snippetId: { default: "" }, name: { default: "" }, fallback: { default: "" } };
  },
  parseHTML() {
    return [{
      tag: "div[data-kb-block='snippet']",
      getAttrs: (el) => ({
        snippetId: el.getAttribute("data-kb-snippet") || "",
        name: el.getAttribute("data-kb-title") || "",
        fallback: el.innerHTML || "",
      }),
    }];
  },
  renderHTML({ HTMLAttributes: a }) {
    return ["div", {
      class: "kb-snippet", "data-kb-block": "snippet",
      "data-kb-snippet": a.snippetId || "", "data-kb-title": a.name || "",
    }, ["div", { class: "kb-snippet-body" },
      ["p", { class: "kb-snippet-label" }, `Reusable block: ${a.name || a.snippetId}`]]];
  },
  addCommands() {
    return {
      insertSnippetRef: (attrs) => ({ commands }) => commands.insertContent({
        type: this.name, attrs,
      }),
    };
  },
});

/** An interactive HTML/JS block. The snippet is stored base64-encoded in
 *  `data-kb-html` and only ever runs inside a sandboxed iframe — see
 *  lib/kbHtmlSandbox.js. */
export const KbHtmlBlock = Node.create({
  name: "kbHtmlBlock",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return { html: { default: "" }, height: { default: 320 }, title: { default: "" } };
  },
  parseHTML() {
    return [{
      tag: "div[data-kb-block='html']",
      getAttrs: (el) => ({
        html: el.getAttribute("data-kb-html") || "",
        height: Number(el.getAttribute("data-kb-height")) || 320,
        title: el.getAttribute("data-kb-title") || "",
      }),
    }];
  },
  renderHTML({ HTMLAttributes: a }) {
    return ["div", {
      class: "kb-html-block", "data-kb-block": "html",
      "data-kb-html": a.html || "", "data-kb-height": String(a.height || 320),
      ...(a.title ? { "data-kb-title": a.title } : {}),
    }, ["div", { class: "kb-html-placeholder" },
      a.title ? `Interactive block — ${a.title}` : "Interactive block"]];
  },
  addCommands() {
    return {
      insertHtmlBlock: (attrs) => ({ commands }) => commands.insertContent({
        type: this.name, attrs,
      }),
      updateHtmlBlock: (attrs) => ({ commands }) => commands.updateAttributes(this.name, attrs),
    };
  },
});
